import React, { useState } from 'react';
import Header from '../components/Header';
import { 
  PhoneIcon,
  PlayIcon,
  SpeakerWaveIcon,
  ChatBubbleLeftRightIcon,
  CloudIcon,
  CpuChipIcon,
  ServerIcon,
  BellIcon,
  ArrowRightIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  BookOpenIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const ConverseFlows = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const nodeTypes = [
    {
      type: 'On Call Picked',
      icon: PhoneIcon,
      description: 'Where your call flow starts when a customer answers',
      features: ['Welcome flows', 'Call verification', 'Initial routing'],
      connections: ['next → Continue to welcome message', 'onCallDisconnect → Handle immediate hangup']
    },
    {
      type: 'Say',
      icon: SpeakerWaveIcon,
      description: 'Speaks text to the customer using text-to-speech',
      features: ['Multi-language TTS', 'Custom messages', 'Instructions'],
      connections: ['in → From previous nodes', 'next → Continue to next action', 'onCallDisconnect → Handle hangup during message']
    },
    {
      type: 'Prompt',
      icon: ChatBubbleLeftRightIcon,
      description: 'Collects input from customer via phone keypad or voice',
      features: ['Menu selection', 'Account number entry', 'Choice collection'],
      connections: ['option:1,2,3 → Different paths based on input', 'timeout → Handle no response']
    },
    {
      type: 'Play',
      icon: PlayIcon,
      description: 'Plays prerecorded audio files to the customer',
      features: ['Hold music', 'Company announcements', 'Recorded instructions'],
      connections: ['in → From previous nodes', 'next → Continue after playback']
    },
    {
      type: 'API Callback',
      icon: ServerIcon,
      description: 'Makes HTTP calls to your external services during the call',
      features: ['Customer lookup', 'Payment processing', 'Data validation'],
      connections: ['in → From previous nodes', 'next → Continue after successful API call']
    },
    {
      type: 'Data Storage',
      icon: CloudIcon,
      description: 'Stores call data and creates API endpoints for retrieval',
      features: ['Call summaries', 'Customer interactions', 'Report generation'],
      connections: ['in → From previous nodes', 'next → Continue to next node']
    }
  ];

  const codeExamples = {
    init: `curl -X POST https://api.taar.ai/converse/your_flow_id/init \\
  -H "Authorization: Bearer <your_token>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customerId": "CUST123",
    "phoneNumber": "+1234567890",
    "email": "customer@example.com",
    "language": "en"
  }'`,
    js: `async function startCallFlow(customerData) {
  const response = await fetch('https://api.taar.ai/converse/support_flow_v1/init', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer <your_token>',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      customerId: customerData.id,
      phoneNumber: customerData.phone,
      email: customerData.email,
      language: 'en'
    })
  });
  
  const result = await response.json();
  
  if (!response.ok) {
    throw new Error(\`Flow failed: \${result.error}\`);
  }
  
  return result.callId;
}`,
    response: `{
  "callId": "call-02a4c9",
  "flowId": "flow_1758461153931_xvyzs3zzl",
  "status": "initiated",
  "endpoints": {
    "5": "https://api.taar.ai/converse/calls/call-02a4c9/c2044989-aa34-475c-8a4a-77ee38831e39",
    "12": "https://api.taar.ai/converse/calls/call-02a4c9/8b8e7d3d-1d7b-4f0a-9a73-9f9c9e5ab123"
  },
  "errors": []
}`,
    error: `{
  "error": "Schema validation failed",
  "details": "Field 'customerType' is required but not provided",
  "missing_fields": ["customerType"],
  "provided_fields": ["customerId", "phoneNumber", "email"]
}`
  };

  const examples = [
    {
      title: "Basic Customer Service Flow",
      description: "A simple IVR system with menu options",
      flow: "Welcome → Menu → Route → Hangup",
      useCase: "Handle customer routing without agent intervention"
    },
    {
      title: "Customer Lookup Flow", 
      description: "Authentication and data retrieval",
      flow: "Welcome → Get Account → API Lookup → Branch → Hangup",
      useCase: "Verify customer identity and retrieve account details"
    },
    {
      title: "Survey Flow",
      description: "Collect feedback and ratings",
      flow: "Intro → Question 1 → Question 2 → Store Data → Thank → Hangup", 
      useCase: "Post-call customer satisfaction surveys"
    }
  ];

  const headerActions = [
    {
      href: "https://app.taar.ai/converse",
      target: "_blank",
      rel: "noopener noreferrer",
      className: "bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors",
      children: "Try Now"
    },
    {
      href: "https://app.taar.ai",
      target: "_blank",
      rel: "noopener noreferrer",
      className: "text-gray-500 hover:text-gray-700",
      children: "Dashboard"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        subtitle="Converse Flows"
        actions={headerActions}
      />

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'nodes', label: 'Node Types' },
              { id: 'api', label: 'API Reference' },
              { id: 'examples', label: 'Examples' },
              { id: 'status', label: 'Platform Status' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Converse Flows
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Build sophisticated voice call flows using a visual drag-and-drop editor. 
                Create conversations by connecting visual blocks instead of writing complex code.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <CpuChipIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Visual Builder</h3>
                  <p className="text-sm text-gray-600">Drag-drop interface for flow creation</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <SpeakerWaveIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Multi-Language TTS</h3>
                  <p className="text-sm text-gray-600">English, Hindi, Bengali, Tamil, Telugu</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <ServerIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">API Integration</h3>
                  <p className="text-sm text-gray-600">Connect to your existing systems</p>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-500">1</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Design</h3>
                  <p className="text-sm text-gray-600">Drag nodes onto canvas to build your call flow</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-500">2</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Connect</h3>
                  <p className="text-sm text-gray-600">Draw lines between nodes to define conversation paths</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-500">3</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Initialize</h3>
                  <p className="text-sm text-gray-600">Send customer data to start the call</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-500">4</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Execute</h3>
                  <p className="text-sm text-gray-600">Your flow runs automatically when customers call</p>
                </div>
              </div>
            </div>

            {/* Getting Started */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Getting Started</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Quick Setup</h3>
                  <ol className="space-y-3 text-sm text-gray-600">
                    <li className="flex">
                      <span className="font-medium text-red-500 mr-2">1.</span>
                      Get your API token from <code className="bg-gray-100 px-1 rounded">app.taar.ai</code>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-red-500 mr-2">2.</span>
                      Build your flow in the visual editor at <code className="bg-gray-100 px-1 rounded">app.taar.ai/converse</code>
                    </li>
                    <li className="flex">
                      <span className="font-medium text-red-500 mr-2">3.</span>
                      Test your flow using the initialization API
                    </li>
                    <li className="flex">
                      <span className="font-medium text-red-500 mr-2">4.</span>
                      Handle calls automatically when customers call
                    </li>
                  </ol>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Account Tiers</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Trial Accounts</div>
                        <div className="text-sm text-gray-600">1 CPS rate limit - perfect for testing</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Production Accounts</div>
                        <div className="text-sm text-gray-600">Higher rate limits for enterprise needs</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'nodes' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Node Types</h2>
              <p className="text-gray-600 mb-8">
                Each node represents an action in your call flow. Connect them to build sophisticated conversations.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {nodeTypes.map((node, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-start space-x-4">
                    <node.icon className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{node.type}</h3>
                      <p className="text-gray-600 mb-4">{node.description}</p>
                      
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Features:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {node.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center">
                                <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Connections:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {node.connections.map((connection, idx) => (
                              <li key={idx} className="flex items-center">
                                <ArrowRightIcon className="w-4 h-4 text-gray-400 mr-2" />
                                {connection}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Connection Patterns */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Connection Patterns</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Simple Flow</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <code className="text-sm text-gray-700">
                      On Call Picked → Say Welcome → Prompt Choice → Handle Options → Hangup
                    </code>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Branching Flow</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <code className="text-sm text-gray-700">
                      option:1 → Route to billing department<br/>
                      option:2 → Route to technical support<br/>
                      option:3 → Route to sales team<br/>
                      timeout → Handle non-responsive customers
                    </code>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Error Handling</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <code className="text-sm text-gray-700">
                      onCallDisconnect → Handle customers who hang up<br/>
                      During Welcome → Log hangup, no action needed<br/>
                      During Payment → Save partial payment attempt<br/>
                      During Survey → Store partial responses
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">API Reference</h2>
              <p className="text-gray-600 mb-8">
                Complete API documentation for Converse Flows integration.
              </p>
            </div>

            {/* Authentication */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Authentication</h3>
              <p className="text-gray-600 mb-4">
                All API endpoints require authentication using your company bearer token:
              </p>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg">
                <code>Authorization: Bearer &lt;company_long_lived_token&gt;</code>
                <button
                  onClick={() => copyToClipboard('Authorization: Bearer <company_long_lived_token>', 'auth')}
                  className="ml-4 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Flow Initialization */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Initialize Flow</h3>
              <p className="text-gray-600 mb-4">
                Start a call flow by sending customer data:
              </p>
              
              <h4 className="font-medium text-gray-900 mb-2">Request</h4>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg mb-4 overflow-x-auto">
                <pre className="text-sm">
                  <code>{codeExamples.init}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(codeExamples.init, 'init')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>

              <h4 className="font-medium text-gray-900 mb-2">Success Response</h4>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg mb-4 overflow-x-auto">
                <pre className="text-sm">
                  <code>{codeExamples.response}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(codeExamples.response, 'response')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>

              <h4 className="font-medium text-gray-900 mb-2">Error Response</h4>
              <div className="bg-gray-900 text-red-400 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm">
                  <code>{codeExamples.error}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(codeExamples.error, 'error')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* JavaScript Example */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">JavaScript Integration</h3>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm">
                  <code>{codeExamples.js}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(codeExamples.js, 'js')}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-700 rounded"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Error Handling */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Error Handling</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-red-500 bg-red-50 p-4">
                  <h4 className="font-medium text-red-800 mb-2">400 - Bad Request</h4>
                  <p className="text-red-700 text-sm">
                    Schema mismatch between your flow and API request. Check required fields match your flow's schema declaration.
                  </p>
                </div>
                
                <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">401 - Unauthorized</h4>
                  <p className="text-yellow-700 text-sm">
                    Invalid or missing bearer token. Verify your token from app.taar.ai dashboard.
                  </p>
                </div>
                
                <div className="border-l-4 border-blue-500 bg-blue-50 p-4">
                  <h4 className="font-medium text-blue-800 mb-2">429 - Too Many Requests</h4>
                  <p className="text-blue-700 text-sm">
                    Rate limit exceeded (1 CPS for trial accounts). Implement delays between API calls.
                  </p>
                </div>
              </div>
            </div>

            {/* Rate Limits */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Rate Limits</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Current Limits</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Trial: 1 call per second</li>
                    <li>• Production: Higher limits available</li>
                    <li>• Scope: Per company bearer token</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Implementation</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Add delays between sequential calls</li>
                    <li>• Use async/await for better flow control</li>
                    <li>• Implement retry logic with backoff</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Flow Examples</h2>
              <p className="text-gray-600 mb-8">
                Real-world examples to get you started building sophisticated voice flows.
              </p>
            </div>

            {examples.map((example, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{example.title}</h3>
                <p className="text-gray- mb-4">{example.description}</p>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Flow Pattern</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <code className="text-sm text-gray-700">{example.flow}</code>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Use Case</h4>
                    <p className="text-sm text-gray-600">{example.useCase}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Integration Patterns */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Integration Patterns</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">CRM Integration</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <code className="text-sm text-gray-700">
                      Customer calls → Authentication → CRM lookup → Personalized response → Data storage
                    </code>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Survey Collection</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <code className="text-sm text-gray-700">
                      Post-call → Intro → Questions → Data transformation → Storage → Analytics feed
                    </code>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Payment Processing</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <code className="text-sm text-gray-700">
                      Billing → Authentication → Payment gateway → Confirmation → Receipt generation
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'status' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Platform Status</h2>
              <p className="text-gray-600 mb-8">
                We're building fast and shipping continuously. Here's what's available now and what's coming.
              </p>
            </div>

            {/* Current Features */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3" />
                What We Do Well
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Visual Flow Building</h4>
                      <p className="text-sm text-gray-600">Intuitive drag-and-drop interface</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Quality Monitoring</h4>
                      <p className="text-sm text-gray-600">MOS scoring and connection rate tracking</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Schema Validation</h4>
                      <p className="text-sm text-gray-600">Detailed error messages when things go wrong</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Flexible Node System</h4>
                      <p className="text-sm text-gray-600">Transform data, make API calls, store results</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Webhook Support</h4>
                      <p className="text-sm text-gray-600">Real-time event notifications</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Multi-language TTS</h4>
                      <p className="text-sm text-gray-600">English, Hindi, Bengali, Tamil, Telugu</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coming Soon */}
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <ClockIcon className="w-6 h-6 text-blue-500 mr-3" />
                What We're Building Next
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Real-time Call Monitoring</h4>
                    <p className="text-sm text-gray-600">Live dashboards showing active calls and metrics</p>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Coming in 4-8 weeks</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">SDKs</h4>
                    <p className="text-sm text-gray-600">Official Node.js and Python client libraries</p>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Coming in 4-8 weeks</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Enhanced Analytics</h4>
                    <p className="text-sm text-gray-600">Deep call insights and optimization suggestions</p>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Coming in 3-6 months</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Enterprise Features</h4>
                    <p className="text-sm text-gray-600">Advanced authentication, audit trails, compliance tools</p>
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Coming in 3-6 months</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Limitations */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500 mr-3" />
                Current Limitations - Will Be Fixed
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <XCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Trial Accounts</h4>
                    <p className="text-sm text-gray-600">1 CPS rate limit (production accounts have higher limits)</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <XCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Monitoring</h4>
                    <p className="text-sm text-gray-600">Limited real-time call tracking (call logs dashboard in development)</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <XCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Documentation</h4>
                    <p className="text-sm text-gray-600">Some features move faster than docs - we update weekly</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <XCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Support</h4>
                    <p className="text-sm text-gray-600">Growing our team - response times improving</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Community Message */}
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Voice Matters</h3>
              <p className="text-gray-600 mb-4">
                We're actively shipping based on customer feedback. If something is missing or doesn't work as expected, 
                report it - there's a good chance we'll fix it quickly.
              </p>
              <div className="flex justify-center space-x-4">
                <a 
                  href="https://app.taar.ai/converse" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Start Building
                </a>
                <a 
                  href="https://app.taar.ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Get Demo
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Copy Success Indicator */}
        {copiedCode && (
          <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
            Code copied to clipboard!
          </div>
        )}
      </main>
    </div>
  );
};

export default ConverseFlows;

